export type RestaurantSegment =
   | "Casual Dining"
   | "Fast Food / QSR"
   | "Fast Casual"
   | "Fine Dining"
   | "Bar / Pub"
   | "Café / Bakery"

export interface BenchmarkData {
   minNet: number
   maxNet: number
   minGross: number
   maxGross: number
   maxPrime: number
}

export const BENCHMARKS: Record<RestaurantSegment, BenchmarkData> = {
   "Casual Dining": { minNet: 3.0, maxNet: 6.0, minGross: 65.0, maxGross: 72.0, maxPrime: 65.0 },
   "Fast Food / QSR": { minNet: 6.0, maxNet: 9.0, minGross: 70.0, maxGross: 75.0, maxPrime: 60.0 },
   "Fast Casual": { minNet: 5.0, maxNet: 8.0, minGross: 68.0, maxGross: 73.0, maxPrime: 65.0 },
   "Fine Dining": { minNet: 2.0, maxNet: 5.0, minGross: 65.0, maxGross: 70.0, maxPrime: 70.0 },
   "Bar / Pub": { minNet: 7.0, maxNet: 10.0, minGross: 75.0, maxGross: 80.0, maxPrime: 55.0 },
   "Café / Bakery": { minNet: 4.0, maxNet: 8.0, minGross: 65.0, maxGross: 70.0, maxPrime: 65.0 },
}

export interface ProfitFormData {
   foodSales: number
   bevSales: number
   cateringSales: number
   foodCost: number
   bevCost: number
   laborCost: number
   rentCost: number
   utilitiesCost: number
   marketingCost: number
   otherCost: number
   segment: RestaurantSegment
}

export const DEFAULT_FORM_STATE: ProfitFormData = {
   foodSales: 75000,
   bevSales: 20000,
   cateringSales: 5000,
   foodCost: 28000,
   bevCost: 5000,
   laborCost: 32000,
   rentCost: 8000,
   utilitiesCost: 2500,
   marketingCost: 1500,
   otherCost: 2000,
   segment: "Casual Dining",
}

export interface MetricStatus {
   text: string
   style: string
}

export interface ProfitMetrics {
   totalRevenue: number
   totalExpenses: number
   netProfitValue: number
   totalCOGS: number
   fixedExpenses: number
   grossProfitMargin: number
   netProfitMargin: number
   primeCostRatio: number
   breakEvenAmount: number
   netStatus: MetricStatus
   grossStatus: MetricStatus
   primeStatus: MetricStatus
}

export const FAQ_ITEMS = [
   {
      question: "What is considered a good profit margin for a restaurant?",
      answer:
         "Generally, standard net profit margins range between 3% and 9%. Achieving margins above 10% indicates exceptional cost control, excellent menu engineering, and highly efficient labor scheduling across peak services.",
   },
   {
      question: "What is Prime Cost, and why is it so important?",
      answer:
         "Prime Cost is the sum of your total cost of goods sold (COGS) and dynamic labor costs. It represents the variables you can adjust most easily. Keeping this figure below 60% to 65% of revenue is crucial to having enough cash left to cover fixed costs like rent and overhead.",
   },
   {
      question: "What distinguishes Gross Margin from Net Profit Margin?",
      answer:
         "Gross Margin reflects your profit after accounting only for raw ingredient costs (COGS). Net Margin, on the other hand, is the final figure left over after paying all expenses—including payroll, occupancy, utilities, and marketing.",
   },
   {
      question: "How is a restaurant's Break-Even Point calculated?",
      answer:
         "To find your break-even point, divide your total fixed expenses (rent, utilities, insurance) by your gross margin percentage. This calculates the baseline sales volume your restaurant needs to reach each month just to pay your bills and stay operational.",
   },
   {
      question: "What is considered a healthy Food Cost Percentage?",
      answer:
         "For most full-service restaurants, a healthy food cost percentage is between 28% and 35%. Fast-casual chains can target 25% to 30%, while pubs can often achieve lower food costs because high-margin drink sales help balance out their numbers.",
   },
]
